import { DashcardAccount } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/Dashcards/DashcardAccount';
import { DashcardContract } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/Dashcards/DashcardContract';
import { useDocumentWorkflowDashcard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/Dashcards/useDocumentWorkflowDashcard';
import { DashcardFunds as FundsDashcard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/Dashcards/DashcardFunds';
import { DashcardInvestments as DashcardInvestment } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/Dashcards/DashcardInvestments';
import { DashcardKYC } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/Dashcards/DashcardKYC';
import {
	InvestmentWorkflowStatus,
	KYCFileType,
	KYCWorkflowStatus,
} from '@dealstack/shared/lib/generated';
import { useMemo } from 'react';
import styled from 'styled-components';
import { useManageKYCController } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/useManageKYCController';
import { Box } from '@mantine/core';
import { useManageInvestorDetailsController } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetails/useManageInvestorDetailsController';
import { useKycWorkflowKycSetting } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useKycSetting';

export const DashcardsContainerInvestorDetails = () => {
	const manageInvestorDetailsController = useManageInvestorDetailsController();
	const { investorDetailsData, investorId } = manageInvestorDetailsController;

	const { data: kycSetting } = useKycWorkflowKycSetting(
		investorDetailsData?.activeKycWorkflowId ?? ''
	);

	const isKycRequired = kycSetting?.isKycRequired;

	const { requestedDocument, suppliedDocument } = useDocumentWorkflowDashcard();

	const manageKYCController = useManageKYCController(investorId);
	const investorKycStatus = manageKYCController.investors?.[0]?.status;
	const kycFiles = manageInvestorDetailsController.investorDetailsData?.kycFiles;
	const declarations = manageInvestorDetailsController.investorDetailsData?.declarations;
	const isDeclarationProvided = declarations?.some((declaration) => declaration.checked);
	// We count all declarations as 1 entity here
	const numberOfDeclarationFiles = 1;
	// Forms are DocumentWorkflow-backed; legacy KYCFile FORM rows are not "supplied" documents here.
	const suppliedProofFilesCount =
		kycFiles?.filter((file) => file.type !== KYCFileType.FORM).length ?? 0;
	const numberOfSuppliedFiles = isDeclarationProvided
		? suppliedProofFilesCount + numberOfDeclarationFiles
		: suppliedProofFilesCount;

	const showFundDashCard = useMemo(() => {
		if (!requestedDocument && !isKycRequired && investorDetailsData?.isUserActive) {
			return true;
		}
		return suppliedDocument > 0 || numberOfSuppliedFiles > 0;
	}, [
		requestedDocument,
		suppliedDocument,
		isKycRequired,
		numberOfSuppliedFiles,
		investorDetailsData?.isUserActive,
	]);

	const investmentStatus = investorDetailsData?.investmentStatus as InvestmentWorkflowStatus;

	return (
		<Box mb="2em">
			<StatusCardWrapper>
				{!showFundDashCard && <DashcardAccount />}
				<DashcardInvestment
					investmentStatus={investmentStatus}
					canRelease={investorDetailsData?.canRelease}
					outstandingDocumentsForRelease={
						investorDetailsData?.outstandingDocumentsForRelease
					}
				/>
				{isKycRequired && (
					<DashcardKYC
						kycStatus={investorKycStatus as KYCWorkflowStatus}
						kycWorkflowId={investorDetailsData?.activeKycWorkflowId}
						isPiv={!!investorDetailsData?.pivPerson}
					/>
				)}
				<DashcardContract />
				{showFundDashCard && <FundsDashcard />}
			</StatusCardWrapper>
		</Box>
	);
};

const StatusCardWrapper = styled.div`
	display: flex;
	flex-direction: row;
	gap: 1rem;
	flex: 1;
	> div {
		width: 100%;
	}
	justify-content: space-between;
`;
